import React, { memo, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { makeStyles } from '~/UIKit/Style';
import { Typography } from '~/UIKit';
import { FilterBadge } from '~/Components/Filter';
import { VERTICAL_SEPARATOR, VERTICAL_SEPARATOR_1, WHITE } from '~/Constants/Colors.constants';
import { FONT_SIZE_LARGE } from '~/Constants/UI.constants';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '100%',
  },
  accordion: {
    '&:before': {
      margin: '0 24px',
      background: 'transparent',
      borderBottom: `1px solid ${VERTICAL_SEPARATOR_1}`,
      zIndex: 11,
      [theme.breakpoints.down('md')]: {
        margin: '0 15px',
        borderBottom: `1px solid ${VERTICAL_SEPARATOR}`,
      },
    },
    '&.Mui-expanded:before': {
      opacity: 1,
    },
  },
  title: {
    [theme.breakpoints.down('md')]: {
      fontSize: FONT_SIZE_LARGE,
      lineHeight: 1.55,
      letterSpacing: '0.5px',
    },
  },
  accordionSummary: {
    top: 0,
    width: '100%',
    zIndex: 10,
    padding: '0 20px 1px',
    position: 'sticky',
    background: WHITE,
    '& .MuiAccordionSummary-content': {
      margin: '15px 0 15px 6px',
      maxWidth: '100%',
      overflow: 'hidden',
      [theme.breakpoints.down('md')]: {
        margin: '15px 0 9px 6px',
      },
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      transform: 'rotate(90deg)',
      '&.Mui-expanded': {
        transform: 'rotate(270deg)',
      },
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row-reverse',
      '& .MuiAccordionSummary-expandIconWrapper': {
        transform: 'rotate(0deg)',
        '&.Mui-expanded': {
          transform: 'rotate(90deg)',
        },
      },
    },
  },
  accordionDetails: {
    padding: 0,
  },
  badge: {
    [theme.breakpoints.down('md')]: {
      marginTop: -7,
    },
  },
}));

export interface IFilterGroup {
  title: React.ReactNode;
  elements: React.ReactNode | React.ReactNode[];
  selectedCount?: number;
}

export const FilterGroups = memo(({ groups }: { groups: IFilterGroup[] }) => {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(() => Array(groups.length).fill(false));

  const handleChange = (idx: number) => () => {
    setExpanded(expanded.map((state, i) => (i === idx ? !state : state)));
  };

  return (
    <div className={classes.root}>
      {groups.map(({ title, elements, selectedCount }, idx: number) => (
        <Accordion
          square
          key={String(title)}
          className={classes.accordion}
          elevation={0}
          disableGutters
          expanded={expanded[idx]}
          onChange={handleChange(idx)}
        >
          <AccordionSummary
            aria-controls="panel-content"
            className={classes.accordionSummary}
            expandIcon={<ChevronRight style={{ fontSize: '1.25rem' }} />}
          >
            <FilterBadge className={classes.badge} badgeContent={selectedCount}>
              <Typography variant="h4" className={classes.title}>
                {title}
              </Typography>
            </FilterBadge>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>{elements}</AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
});
